/**
 * StorageUrlService
 *
 * Centralized utility for managing Supabase Storage signed URLs.
 * Provides consistent patterns for generating, validating, and managing
 * storage URLs across all features.
 *
 * ⚠️  ARCHITECTURE LOCK (2026-05-12) — Supabase Storage is the private,
 * RLS-gated half of the hybrid media architecture:
 *   - Public, high-read content (photos, headshots, gallery, post media,
 *     org branding, community-event headers, public documents) → Cloudflare R2
 *     via `r2.service.ts` (zero egress, served from `cdn.ballee.co`).
 *   - Private, RLS-gated content (contracts, identity docs, legal docs,
 *     reimbursements, invoice PDFs, costume photos, production docs,
 *     venue docs, chat attachments) → Supabase Storage (this service).
 *
 * 978 Meteor-uploaded VIDEOS also live in Supabase Storage. Per the locked
 * video architecture (.claude/clauses/critical-rules-video-architecture.md),
 * they stay here until they hit the breakeven view-count for re-encoding
 * via Mux. New video uploads go to Mux + R2 as origin, NOT here.
 *
 * Why this dual-store: $0 egress on R2 for public content (the high-read
 * path) + RLS-aware signed URLs on Supabase for private content (the
 * low-traffic path). Crossover ~250 GB egress/month. Ballee crosses that
 * immediately at any meaningful traffic. See
 * docs/architecture/video-service-decision-framework-2026-05-10.md §4.
 *
 * URL helper behavior: `getProfilePhotoSignedUrl` and friends detect
 * `http(s)://*` paths and PASS THROUGH UNCHANGED (R2 URLs hit the
 * public host directly). Bucket-relative paths get a Supabase signed URL.
 *
 * Best Practices (from Supabase docs):
 * - Use signed URLs for private files to share for a fixed time
 * - Set appropriate expiration times based on use case (≤24h for dancer media)
 * - Use batch operations (createSignedUrls) for multiple files
 * - Always validate storage paths before operations
 *
 * @see https://supabase.com/docs/reference/javascript/storage-from-createsignedurl
 * @see .claude/clauses/critical-rules-video-architecture.md (full architecture lock)
 * @see ./r2.service.ts (public-content counterpart)
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { captureServiceError } from '@kit/shared/lib/sentry-helpers';
import { Result, ServiceError } from '@kit/shared/result';

import {
  extractDeniedObjectName,
  isAlwaysBugDbError,
} from '../services/db-error-classification';
import { resolveLocalCdnFallback } from './local-storage-cdn-fallback';

/**
 * Fire a Sentry capture when a storage error is an "always a bug" DB error
 * (missing GRANT, schema drift) that the Result pattern would otherwise
 * swallow into a failed Result. No-op for ordinary signing failures (no
 * SQLSTATE) and expected authorization outcomes. Does not change the result.
 */
function captureIfAlwaysBugStorageError(
  error: { code?: string; message?: string },
  step: string,
): void {
  if (!isAlwaysBugDbError(error)) return;
  captureServiceError(
    { message: error.message ?? '', code: error.code, details: error },
    {
      action: `db.${error.code}`,
      step,
      deniedObject: extractDeniedObjectName(error.message),
      sqlstate: error.code,
    },
  );
}

/**
 * Storage bucket names used in the application
 *
 * IMPORTANT: Always use these constants instead of hardcoded strings.
 * This ensures type safety and makes bucket name changes easier.
 */
export const StorageBuckets = {
  // Account/Profile
  ACCOUNT_IMAGE: 'account_image',
  PROFILE_MEDIA: 'profile-media',
  DANCER_MEDIA: 'dancer-media',

  // Documents
  VENUE_DOCUMENTS: 'venue-documents',
  PRODUCTION_DOCUMENTS: 'production-documents',
  LEGAL_DOCUMENTS: 'legal-documents',
  /**
   * @deprecated EMPTY in production and read by nothing (verified BAL-657,
   * 2026-07-08: 0 objects). Do NOT route producers here — receipts written to
   * this bucket are unreadable (this was the #20c 404 bug). Use REIMBURSEMENTS.
   * Retained only to avoid a bucket-drop migration; safe to remove once purged.
   */
  REIMBURSEMENT_DOCUMENTS: 'reimbursement-documents',

  // Canonical reimbursement bucket — holds 100% of prod data (803 objects /
  // 801 rows as of 2026-07-08) and is the bucket every reader signs from.
  REIMBURSEMENTS: 'reimbursements',

  // Legal/Compliance
  CONTRACTS: 'contracts',
  IDENTITY_DOCUMENTS: 'identity-documents',
  INVOICE_PDFS: 'invoice-pdfs',

  // Chat
  CHAT_ATTACHMENTS: 'chat-attachments',

  // Marketplace (public-read bucket for listing images). Rows created before
  // migration 20260621000000 still reference CHAT_ATTACHMENTS.
  MARKETPLACE_IMAGES: 'marketplace-images',
} as const;

export type StorageBucket =
  (typeof StorageBuckets)[keyof typeof StorageBuckets];

/**
 * Default expiration times (in seconds) for different use cases
 */
export const SignedUrlExpiry = {
  /** For immediate display in UI (1 hour) */
  IMMEDIATE_DISPLAY: 3600,
  /** For download links (24 hours) */
  DOWNLOAD: 86400,
  /** For profile photos stored in DB (7 days) */
  PROFILE_PHOTO: 604800,
  /** For admin review (24 hours) */
  ADMIN_REVIEW: 86400,
  /** Maximum allowed by Supabase (7 days) */
  MAX: 604800,
  /** Minimum allowed (60 seconds) */
  MIN: 60,
} as const;

/**
 * Options for generating signed URLs
 */
export interface SignedUrlOptions {
  /** Expiration time in seconds (default: 3600) */
  expiresIn?: number;
  /** Whether to use download disposition */
  download?: boolean | string;
  /** Transform options for images */
  transform?: {
    width?: number;
    height?: number;
    quality?: number;
    resize?: 'cover' | 'contain' | 'fill';
    format?: 'origin';
  };
}

/**
 * Result of a batch signed URL operation
 */
export interface BatchSignedUrlResult {
  path: string;
  signedUrl: string | null;
  error: string | null;
}

/**
 * StorageUrlService
 *
 * Provides centralized, type-safe methods for generating signed URLs
 * from Supabase Storage buckets.
 */
export class StorageUrlService {
  constructor(private client: SupabaseClient) {}

  /**
   * Generate a signed URL for a single file
   *
   * @param bucket - Storage bucket name
   * @param storagePath - Path to file in bucket (e.g., "user-id/photos/image.jpg")
   * @param options - URL generation options
   * @returns Result containing signed URL or error
   *
   * @example
   * ```typescript
   * const service = new StorageUrlService(client);
   * const result = await service.getSignedUrl(
   *   StorageBuckets.DANCER_MEDIA,
   *   'user-123/headshot/photo.jpg',
   *   { expiresIn: SignedUrlExpiry.IMMEDIATE_DISPLAY }
   * );
   *
   * if (result.isSuccess) {
   *   const url = result.getValue();
   * }
   * ```
   */
  async getSignedUrl(
    bucket: StorageBucket,
    storagePath: string,
    options: SignedUrlOptions = {},
  ): Promise<Result<string>> {
    // Validate storage path
    if (!storagePath || storagePath.trim() === '') {
      return Result.fail(ServiceError.validation('Storage path is required'));
    }

    // Local-dev prod-mirror: binaries live only on prod Storage; the env-gated
    // helper serves them via the dev proxy (NO-OP / null in production).
    const localFallback = resolveLocalCdnFallback(bucket, storagePath);
    if (localFallback) {
      return Result.ok(localFallback);
    }

    // Validate and normalize expiration
    const expiresIn = this.normalizeExpiry(options.expiresIn);
    if (expiresIn instanceof ServiceError) {
      return Result.fail(expiresIn);
    }

    try {
      const { data, error } = await this.client.storage
        .from(bucket)
        .createSignedUrl(storagePath, expiresIn, {
          download: options.download,
          transform: options.transform,
        });

      if (error || !data?.signedUrl) {
        if (error) {
          captureIfAlwaysBugStorageError(error, 'storage.getSignedUrl');
        }
        return Result.fail(
          ServiceError.database(
            error?.message || 'Failed to generate signed URL',
            error,
          ),
        );
      }

      return Result.ok(data.signedUrl);
    } catch (error) {
      return Result.fail(
        ServiceError.internal(
          error instanceof Error
            ? error.message
            : 'Failed to generate signed URL',
          error,
        ),
      );
    }
  }

  /**
   * Generate signed URLs for multiple files in batch
   *
   * More efficient than multiple single calls when fetching multiple documents.
   *
   * @param bucket - Storage bucket name
   * @param storagePaths - Array of file paths
   * @param options - URL generation options (applied to all)
   * @returns Array of results with path, URL, and any errors
   *
   * @example
   * ```typescript
   * const results = await service.getBatchSignedUrls(
   *   StorageBuckets.VENUE_DOCUMENTS,
   *   ['venue-1/doc1.pdf', 'venue-1/doc2.pdf'],
   *   { expiresIn: SignedUrlExpiry.DOWNLOAD }
   * );
   *
   * results.forEach(r => {
   *   if (r.signedUrl) console.log(`${r.path}: ${r.signedUrl}`);
   * });
   * ```
   */
  async getBatchSignedUrls(
    bucket: StorageBucket,
    storagePaths: string[],
    options: SignedUrlOptions = {},
  ): Promise<BatchSignedUrlResult[]> {
    if (!storagePaths.length) {
      return [];
    }

    // Filter out empty paths
    const validPaths = storagePaths.filter((p) => p && p.trim() !== '');
    if (!validPaths.length) {
      return storagePaths.map((path) => ({
        path,
        signedUrl: null,
        error: 'Invalid storage path',
      }));
    }

    // Local-dev prod-mirror: resolve any path the dev fallback covers up front
    // (NO-OP / null in production), and only sign the remainder against the
    // local bucket. Keeps the "one signed-URL batch" contract for the rest.
    const localByPath = new Map<string, string>();
    for (const p of validPaths) {
      const local = resolveLocalCdnFallback(bucket, p);
      if (local) localByPath.set(p, local);
    }
    const pathsToSign = validPaths.filter((p) => !localByPath.has(p));
    if (!pathsToSign.length) {
      return storagePaths.map((path) => ({
        path,
        signedUrl: localByPath.get(path) ?? null,
        error: localByPath.has(path) ? null : 'Invalid storage path',
      }));
    }

    const expiresIn = this.normalizeExpiry(options.expiresIn);
    if (expiresIn instanceof ServiceError) {
      return storagePaths.map((path) => ({
        path,
        signedUrl: null,
        error: expiresIn.message,
      }));
    }

    try {
      const { data, error } = await this.client.storage
        .from(bucket)
        .createSignedUrls(pathsToSign, expiresIn, {
          download: options.download ?? false,
        });

      if (error) {
        captureIfAlwaysBugStorageError(error, 'storage.getSignedUrls');
        // Signing failed, but dev-fallback paths still resolve.
        return storagePaths.map((path) => ({
          path,
          signedUrl: localByPath.get(path) ?? null,
          error: localByPath.has(path) ? null : error.message,
        }));
      }

      // Map signed results back, then overlay the dev-fallback URLs.
      const resultMap = new Map<string, string>(localByPath);
      (data || []).forEach((item) => {
        if (item.signedUrl && item.path) {
          resultMap.set(item.path, item.signedUrl);
        }
      });

      return storagePaths.map((path) => ({
        path,
        signedUrl: resultMap.get(path) || null,
        error: resultMap.has(path) ? null : 'Failed to generate URL',
      }));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Batch URL generation failed';
      return storagePaths.map((path) => ({
        path,
        signedUrl: null,
        error: errorMessage,
      }));
    }
  }

  /**
   * Get a public URL for a file (no authentication required)
   *
   * WARNING: Only use for truly public files. Most documents should use signed URLs.
   *
   * @param bucket - Storage bucket name
   * @param storagePath - Path to file in bucket
   * @returns Public URL string
   */
  getPublicUrl(bucket: StorageBucket, storagePath: string): string {
    // Local-dev prod-mirror fallback (NO-OP in production).
    const localFallback = resolveLocalCdnFallback(bucket, storagePath);
    if (localFallback) return localFallback;
    const { data } = this.client.storage.from(bucket).getPublicUrl(storagePath);
    return data.publicUrl;
  }

  /**
   * Enrich an array of items with signed URLs
   *
   * Generic helper to add signed URLs to any array of objects with storage paths.
   *
   * @param bucket - Storage bucket name
   * @param items - Array of items to enrich
   * @param pathExtractor - Function to extract storage path from item
   * @param urlSetter - Function to set the signed URL on item
   * @param options - URL generation options
   * @returns Array of enriched items
   *
   * @example
   * ```typescript
   * const documents = await service.enrichWithSignedUrls(
   *   StorageBuckets.VENUE_DOCUMENTS,
   *   docs,
   *   (doc) => doc.storage_path,
   *   (doc, url) => ({ ...doc, signedUrl: url }),
   *   { expiresIn: SignedUrlExpiry.DOWNLOAD }
   * );
   * ```
   */
  async enrichWithSignedUrls<T, R>(
    bucket: StorageBucket,
    items: T[],
    pathExtractor: (item: T) => string | null | undefined,
    urlSetter: (item: T, url: string | null) => R,
    options: SignedUrlOptions = {},
  ): Promise<R[]> {
    if (!items.length) {
      return [];
    }

    // Extract all paths
    const pathsWithIndices: { path: string; index: number }[] = [];
    items.forEach((item, index) => {
      const path = pathExtractor(item);
      if (path) {
        pathsWithIndices.push({ path, index });
      }
    });

    // Get batch signed URLs for items that have paths
    const pathsToFetch = pathsWithIndices.map((p) => p.path);
    const urlResults =
      pathsToFetch.length > 0
        ? await this.getBatchSignedUrls(bucket, pathsToFetch, options)
        : [];

    // Create map of path -> signedUrl
    const urlMap = new Map<string, string | null>();
    urlResults.forEach((result) => {
      urlMap.set(result.path, result.signedUrl);
    });

    // Apply URLs to items
    return items.map((item) => {
      const path = pathExtractor(item);
      const url = path ? urlMap.get(path) || null : null;
      return urlSetter(item, url);
    });
  }

  /**
   * Normalize and validate expiration time
   */
  private normalizeExpiry(expiresIn?: number): number | ServiceError {
    const expiry = expiresIn ?? SignedUrlExpiry.IMMEDIATE_DISPLAY;

    if (expiry < SignedUrlExpiry.MIN) {
      return ServiceError.validation(
        `Expiration must be at least ${SignedUrlExpiry.MIN} seconds`,
      );
    }

    if (expiry > SignedUrlExpiry.MAX) {
      return ServiceError.validation(
        `Expiration cannot exceed ${SignedUrlExpiry.MAX / 86400} days`,
      );
    }

    return expiry;
  }
}

/**
 * Factory function to create StorageUrlService instance
 */
export function createStorageUrlService(
  client: SupabaseClient,
): StorageUrlService {
  return new StorageUrlService(client);
}
